import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box, Paper, Typography, Button, Alert, Chip, Grid, TextField,
  CircularProgress, Divider
} from "@mui/material";
import {
  CloudUpload, CheckCircle, Error as ErrorIcon, Business, VpnKey, Refresh
} from "@mui/icons-material";

const API_URL = process.env.REACT_APP_API_URL || "";

export default function ConfiguracionEmpresa() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [mensaje, setMensaje] = useState(null);

  // Firma upload
  const [firmaFile, setFirmaFile] = useState(null);
  const [firmaPassword, setFirmaPassword] = useState("");

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/empresa/configuracion`, {
        headers: getAuthHeaders(),
      });
      setConfig(res.data);
    } catch (err) {
      console.error("Error cargando configuración:", err);
      setMensaje({ tipo: "error", texto: "Error al cargar la configuración" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const handleSubirFirma = async (e) => {
    e.preventDefault();
    if (!firmaFile) return setMensaje({ tipo: "error", texto: "Seleccione un archivo .p12" });
    if (!firmaPassword) return setMensaje({ tipo: "error", texto: "Ingrese la contraseña de la firma" });

    setUploading(true);
    setMensaje(null);

    const formData = new FormData();
    formData.append("firma", firmaFile);
    formData.append("password", firmaPassword);

    try {
      await axios.post(`${API_URL}/api/usuarios/firma`, formData, {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "multipart/form-data",
        },
      });
      setMensaje({ tipo: "success", texto: "Firma electrónica subida correctamente" });
      setFirmaFile(null);
      setFirmaPassword("");
      fetchConfig(); // Refrescar estado
    } catch (err) {
      setMensaje({
        tipo: "error",
        texto: err.response?.data?.error || "Error al subir la firma",
      });
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const empresa = config?.empresa;
  const firma = config?.firma;

  return (
    <Box sx={{ p: 3, maxWidth: 1000, mx: "auto" }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Mi Empresa
      </Typography>

      {mensaje && (
        <Alert severity={mensaje.tipo} sx={{ mb: 2 }} onClose={() => setMensaje(null)}>
          {mensaje.texto}
        </Alert>
      )}

      {/* Datos de la empresa */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <Business color="primary" /> Datos de la Empresa
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="caption" color="text.secondary">Razón Social</Typography>
            <Typography variant="body1" fontWeight={600}>{empresa?.razonSocial || "—"}</Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Typography variant="caption" color="text.secondary">RUC</Typography>
            <Typography variant="body1" fontWeight={600} fontFamily="monospace">{empresa?.ruc || "—"}</Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Typography variant="caption" color="text.secondary">Nombre Comercial</Typography>
            <Typography variant="body1">{empresa?.nombreComercial || "—"}</Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="caption" color="text.secondary">Dirección</Typography>
            <Typography variant="body2">{empresa?.direccion || "—"}</Typography>
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <Typography variant="caption" color="text.secondary">Establecimiento</Typography>
            <Typography variant="body1" fontWeight={600}>{empresa?.establecimiento || "—"}</Typography>
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <Typography variant="caption" color="text.secondary">Pto. Emisión</Typography>
            <Typography variant="body1" fontWeight={600}>{empresa?.ptoEmision || "—"}</Typography>
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <Typography variant="caption" color="text.secondary">Ambiente</Typography>
            <Chip label={empresa?.ambiente === "2" ? "PRODUCCIÓN" : "PRUEBAS"} color={empresa?.ambiente === "2" ? "success" : "warning"} size="small" />
          </Grid>
        </Grid>
      </Paper>

      {/* Firma Electrónica */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h6" fontWeight={600} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <VpnKey color="primary" /> Firma Electrónica
          </Typography>
          {firma?.configurada && firma?.archivoExiste ? (
            <Chip icon={<CheckCircle />} label="Configurada" color="success" size="small" />
          ) : (
            <Chip icon={<ErrorIcon />} label="No configurada" color="warning" size="small" />
          )}
        </Box>

        {firma?.configurada && firma?.archivoExiste && (
          <Alert severity="success" sx={{ mb: 2 }}>Firma lista para firmar comprobantes.</Alert>
        )}

        <Box component="form" onSubmit={handleSubirFirma} sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
          <Button variant="outlined" component="label" startIcon={<CloudUpload />}>
            {firmaFile ? firmaFile.name : "Seleccionar .p12"}
            <input type="file" accept=".p12" hidden onChange={(e) => setFirmaFile(e.target.files[0])} />
          </Button>
          <TextField size="small" type="password" label="Contraseña" value={firmaPassword}
            onChange={(e) => setFirmaPassword(e.target.value)} sx={{ width: 180 }} />
          <Button type="submit" variant="contained" disabled={uploading || !firmaFile || !firmaPassword}
            startIcon={uploading ? <CircularProgress size={18} /> : <CloudUpload />}>
            {uploading ? "Subiendo..." : "Subir"}
          </Button>
        </Box>
      </Paper>

      {/* Categorías y Marcas */}
      <CategoriasMarcasSection getAuthHeaders={getAuthHeaders} />
    </Box>
  );
}

// Sección inline de Categorías y Marcas
function CategoriasMarcasSection({ getAuthHeaders }) {
  const [categorias, setCategorias] = useState([]);
  const [categoriasServ, setCategoriasServ] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [catInput, setCatInput] = useState("");
  const [catServInput, setCatServInput] = useState("");
  const [marcaInput, setMarcaInput] = useState("");

  useEffect(() => { cargar(); }, []);

  const cargar = async () => {
    try {
      const [catRes, catServRes, marRes] = await Promise.all([
        axios.get(`${API_URL}/api/catalogo/categorias`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/catalogo/categorias-servicios`, { headers: getAuthHeaders() }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/catalogo/marcas`, { headers: getAuthHeaders() })
      ]);
      setCategorias(Array.isArray(catRes.data) ? catRes.data : []);
      setCategoriasServ(Array.isArray(catServRes.data) ? catServRes.data : []);
      setMarcas(Array.isArray(marRes.data) ? marRes.data : []);
    } catch {}
  };

  const agregarCat = async () => { if (!catInput.trim()) return; try { await axios.post(`${API_URL}/api/catalogo/categorias`, { nombre: catInput }, { headers: getAuthHeaders() }); setCatInput(""); cargar(); } catch {} };
  const eliminarCat = async (id) => { try { await axios.delete(`${API_URL}/api/catalogo/categorias/${id}`, { headers: getAuthHeaders() }); cargar(); } catch {} };
  const agregarCatServ = async () => { if (!catServInput.trim()) return; try { await axios.post(`${API_URL}/api/catalogo/categorias-servicios`, { nombre: catServInput }, { headers: getAuthHeaders() }); setCatServInput(""); cargar(); } catch {} };
  const eliminarCatServ = async (id) => { try { await axios.delete(`${API_URL}/api/catalogo/categorias-servicios/${id}`, { headers: getAuthHeaders() }); cargar(); } catch {} };
  const agregarMarca = async () => { if (!marcaInput.trim()) return; try { await axios.post(`${API_URL}/api/catalogo/marcas`, { nombre: marcaInput }, { headers: getAuthHeaders() }); setMarcaInput(""); cargar(); } catch {} };
  const eliminarMarca = async (id) => { try { await axios.delete(`${API_URL}/api/catalogo/marcas/${id}`, { headers: getAuthHeaders() }); cargar(); } catch {} };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>🏷️ Categorías y Marcas</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Se usan en Productos y Servicios para clasificarlos
      </Typography>

      <Grid container spacing={3}>
        {/* Categorías Productos */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Categorías (Productos)</Typography>
          <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
            <TextField size="small" fullWidth placeholder="Nueva categoría" value={catInput}
              onChange={(e) => setCatInput(e.target.value.toUpperCase())}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); agregarCat(); } }} />
            <Button variant="contained" size="small" onClick={agregarCat} sx={{ minWidth: 40 }}>+</Button>
          </Box>
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            {categorias.map(c => <Chip key={c.id} label={c.nombre} onDelete={() => eliminarCat(c.id)} size="small" variant="outlined" />)}
            {categorias.length === 0 && <Typography variant="caption" color="text.secondary">Sin categorías</Typography>}
          </Box>
        </Grid>

        {/* Categorías Servicios */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Categorías (Servicios)</Typography>
          <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
            <TextField size="small" fullWidth placeholder="Nueva categoría servicio" value={catServInput}
              onChange={(e) => setCatServInput(e.target.value.toUpperCase())}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); agregarCatServ(); } }} />
            <Button variant="contained" size="small" color="secondary" onClick={agregarCatServ} sx={{ minWidth: 40 }}>+</Button>
          </Box>
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            {categoriasServ.map(c => <Chip key={c.id} label={c.nombre} onDelete={() => eliminarCatServ(c.id)} size="small" variant="outlined" color="secondary" />)}
            {categoriasServ.length === 0 && <Typography variant="caption" color="text.secondary">Sin categorías</Typography>}
          </Box>
        </Grid>

        {/* Marcas */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Marcas (Productos)</Typography>
          <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
            <TextField size="small" fullWidth placeholder="Nueva marca" value={marcaInput}
              onChange={(e) => setMarcaInput(e.target.value.toUpperCase())}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); agregarMarca(); } }} />
            <Button variant="contained" size="small" onClick={agregarMarca} sx={{ minWidth: 40 }}>+</Button>
          </Box>
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            {marcas.map(m => <Chip key={m.id} label={m.nombre} onDelete={() => eliminarMarca(m.id)} size="small" variant="outlined" />)}
            {marcas.length === 0 && <Typography variant="caption" color="text.secondary">Sin marcas</Typography>}
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
}
